import { BottomTabScreenProps, createBottomTabNavigator } from "@react-navigation/bottom-tabs"
import { CompositeScreenProps, getFocusedRouteNameFromRoute } from "@react-navigation/native"
import React from "react"
import { AppStackParamList, AppStackScreenProps } from "./AppNavigator"
import Icon from "@expo/vector-icons/MaterialCommunityIcons"
import { typography } from "../theme/typography"
import { colors } from "colors"
import { HomeStack } from "./UserStack/HomeStack"
import { Platform } from "react-native"
import { translate } from "i18n"
import { ProfileStack } from "./UserStack/ProfileStack"
import { NewsStack } from "./UserStack/NewsStack"
import { VoucherCombineStack } from "./UserStack/VoucherCombineStack"
import { useColor } from "app/theme/getThemes"
import { observer } from "mobx-react-lite"
import { useStores } from "models"
import { View } from "react-native"

export type TabParamList = {
  QRStack: undefined
  rewardStack: undefined
  homeStack: undefined
  memberStack: undefined
  voucherStack: undefined
  newsStack: undefined
  profileStack: undefined
  voucherCombineStack: undefined
  StampingStack: undefined
}

/**
 * Helper for automatically generating navigation prop types for each route.
 *
 * More info: https://reactnavigation.org/docs/typescript/#organizing-types
 */
export type TabScreenProps<T extends keyof TabParamList> = CompositeScreenProps<
  BottomTabScreenProps<TabParamList, T>,
  AppStackScreenProps<keyof AppStackParamList>
>

const Tab = createBottomTabNavigator<TabParamList>()

const VoucherTabIcon = observer(({ color, size }: { color: string, size: number }) => {
  const { couponStore } = useStores()
  return (
    <View>
      <Icon color={color} name="gift-outline" size={size} />

      {couponStore.isShowBadge && (
        <View
          style={{
            position: "absolute",
            right: -6,
            top: -3,
            backgroundColor: "red",
            borderRadius: 100,
            width: 12,
            height: 12,
          }}
        />
      )}
    </View>
  )
})

function getTabBarVisibility(route: any) {
  const routeName = getFocusedRouteNameFromRoute(route) ?? "home"

  const hiddenRoutes = [
    "editProfile",
    "referral",
    "redeem",
    "pointHistory",
    "voucher",
    "voucherDetail",
    "coupon",
    "couponDetail",
    "uploadReceipt",
    "notification",
    "feedback",
    "memberShip",
    "member",
    "rewards",
    "rewardDetail",
    "forYouDetails",
    "editPin",
    "memberQr",
    "stamps",
    "stampsDetails",
    "promotionHome",
    "promotionDetail",
    "foryou",
  ]

  return hiddenRoutes.includes(routeName) ? "none" : "flex"
}

export function TabNavigator() {
  const { couponStore } = useStores()
  const iconSize = Platform.OS === "android" ? 23 : 25
  const { companyTheme } = useColor()

  return (
    <Tab.Navigator
      initialRouteName="homeStack"
      screenOptions={({ route }) => {
        return {
          headerShown: false,
          tabBarHideOnKeyboard: true,
          tabBarItemStyle: { marginHorizontal: 10 },
          tabBarActiveTintColor: companyTheme.first,
          tabBarInactiveTintColor: colors.placeholder,
          tabBarLabelStyle: {
            fontFamily: typography.primary.normal,
            fontWeight: "bold",
            fontSize: 12,
          },
          tabBarStyle: {
            display: getTabBarVisibility(route),
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderWidth: 0,
            borderColor: "transparent",
            marginTop: -15,
          },
        }
      }}
    >
      <Tab.Screen
        name="homeStack"
        component={HomeStack}
        options={() => ({
          tabBarLabel: translate("homeScreen.title"),
          tabBarIcon: ({ color }) => <Icon color={color} name="home-outline" size={iconSize} />,
        })}
      />
      <Tab.Screen
        name="voucherCombineStack"
        component={VoucherCombineStack}
        options={() => ({
          // tabBarLabel: translate("redeemScreen.title"),
          tabBarLabel: "Redemption",
          tabBarIcon: ({ color }) => {
            return (
              <>
                <VoucherTabIcon color={color} size={iconSize} />
              </>
            )
          },
        })}
        listeners={{
          tabPress: (e) => {
            couponStore.hideBadge()
          }
        }}
      />
      <Tab.Screen
        name="newsStack"
        component={NewsStack}
        options={() => ({
          // tabBarLabel: translate("redeemScreen.title"),
          tabBarLabel: "Latest",
          tabBarIcon: ({ color }) => <Icon color={color} name="newspaper-check" size={iconSize} />,
        })}
      />
      <Tab.Screen
        name="profileStack"
        component={ProfileStack}
        options={() => ({
          tabBarLabel: translate("profileScreen.title"),
          tabBarIcon: ({ color }) => (
            <Icon color={color} name="account-circle-outline" size={iconSize} />
          ),
        })}
      />
    </Tab.Navigator>
  )
}
